import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';

import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import {
  getGermanAccountFieldDelta,
  getGermanBranchFieldDelta,
} from '../../../molecules/SubmitCredentialsScreen/germanSplitProviderField';
import { isString } from '../../../../../utilities/utils';
import { ProviderField } from '../../../../../models/provider';
import { calculateProviderInputs } from '../../PersistedProviderInputsProvider/PersistedProviderInputsProvider';
import { Caption } from '../../../../atoms/Caption/Caption';
import { ProviderInputValidator, ValidationValues } from './ProviderInputValidator';
import { Input, OnChangeProps, ProviderInputState } from './Input/Input';
import { StyledForm, StyledInputsContainer } from './ProviderInputs.styles';
import {
  PROVIDER_INPUT_FORM_ID,
  ProviderInputField,
  ProviderInputsProps,
  SubmitProviderInputsPayload,
} from './ProviderInputs';

export const splitGermanFieldIntoTwo = (oldGermanField: ProviderField): ProviderField[] => {
  const germanBranchField = {
    ...oldGermanField,
    ...getGermanBranchFieldDelta(),
  };
  const germanAccountField = {
    ...oldGermanField,
    ...getGermanAccountFieldDelta(),
  };

  return [germanBranchField, germanAccountField];
};

export const ProviderInputsGermanyPresenter: React.FC<ProviderInputsProps> = ({
  providerFields: initialProviderFields,
  intl,
  children,
  fixedValue,
  renderProviderFields = true,
  showPinCodeInput,
  formId = PROVIDER_INPUT_FORM_ID,
  onSubmit,
  setButtonDisabled,
}) => {
  const [errorCaption, setErrorCaption] = useState('');
  const [providerInputs, setProviderInputs] = useState<ProviderInputField>({});
  const [providerFields, setProviderFields] = useState<ProviderField[]>(initialProviderFields);
  const oldGermanInputField = useMemo(() => initialProviderFields[0], [initialProviderFields]);

  const inputRefs = useRef<React.RefObject<HTMLInputElement>[]>([]);

  const focusInput = (index: number) => {
    const inputRef = inputRefs.current[index];

    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    const newProviderFields = splitGermanFieldIntoTwo(oldGermanInputField);

    setProviderFields(newProviderFields);
  }, [initialProviderFields, oldGermanInputField]);

  useEffect(() => {
    const isAllInputsValid = !Object.values(providerInputs)
      .map((input) => input.isValid)
      .includes(false);

    if (!renderProviderFields || isAllInputsValid) {
      setButtonDisabled(false);

      return;
    }
    setButtonDisabled(true);
  }, [providerInputs, renderProviderFields, setButtonDisabled]);

  useEffect(
    () => {
      setProviderInputs(calculateProviderInputs(providerFields, fixedValue));
    },
    // Execute recalculation every time `providerFields` change.
    // In supplemental information scenario we want to display always latest 'Security code'.
    [providerFields, fixedValue]
  );

  const getErrorMessage = ({ errorMessage, errorMessageValues }: ValidationValues) => {
    if (!errorMessage) {
      return '';
    }

    return isString(errorMessage)
      ? errorMessage
      : intl.formatMessage(errorMessage, errorMessageValues);
  };

  const validateInput = ({ providerInputName, value, onFormSubmit }: OnChangeProps) => {
    const inputProvider = providerInputs[providerInputName];

    const { onSubmit: onSubmitOperation, onType } = new ProviderInputValidator(
      inputProvider.providerField
    ).validate(value);

    const isValidInput = Boolean(inputProvider.providerField.pattern && onSubmitOperation.isValid);

    const providerInputState: ProviderInputState = {
      ...(onFormSubmit
        ? {
            ...onSubmitOperation,
            errorMessage: getErrorMessage(onSubmitOperation),
          }
        : {
            ...onType,
            errorMessage: getErrorMessage(onType),
          }),
      value,
      disabled: inputProvider.disabled,
      providerField: inputProvider.providerField,
      showCheckIcon: isValidInput,
    };

    setProviderInputs((inputs) => ({
      ...inputs,
      [providerInputName]: providerInputState,
    }));

    if (!isValidInput && providerInputState.errorMessage) {
      setErrorCaption(providerInputState.errorMessage);
    } else {
      setErrorCaption('');
    }

    const autoFocusNextInputField = isValidInput;

    if (autoFocusNextInputField) {
      const nextInputFieldIndex =
        Object.values(providerInputs).indexOf(providerInputs[providerInputName]) + 1;
      focusInput(nextInputFieldIndex);
    }

    return providerInputState;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!renderProviderFields) {
      onSubmit({});

      return;
    }

    const validatedInputs = Object.keys(providerInputs).map((fieldName) => {
      const inputProvider = providerInputs[fieldName];
      const { value } = inputProvider;

      return validateInput({ providerInputName: fieldName, value, onFormSubmit: true });
    });

    const invalidFieldIndex = validatedInputs.map((input) => input.isValid).indexOf(false);

    if (invalidFieldIndex !== -1) {
      // Focus first invalid input.
      focusInput(invalidFieldIndex);

      return;
    }

    // Blur all the inputs to hide software keyboard on mobile devices.
    inputRefs.current.forEach((inputReference) => {
      if (inputReference.current) {
        inputReference.current.blur();
      }
    });

    const inputNameValuePairs: SubmitProviderInputsPayload = {};

    validatedInputs.forEach((field) => {
      inputNameValuePairs[field.providerField.name] = field.value;
    });

    onSubmit({
      [oldGermanInputField.name]: Object.values(inputNameValuePairs).join(''),
    });
  };

  return (
    <StyledForm id={formId} onSubmit={handleSubmit} data-testid={formId}>
      {renderProviderFields && providerFields.length > 0 && (
        <StyledInputsContainer flexRow>
          {providerFields.map(({ name: fieldName }, index) => {
            const providerInputState = providerInputs[fieldName];

            if (!providerInputState) {
              // the initial field state have not yet been calculated by the effect
              return null;
            }

            // Store a ref, so we can focus the `input` element later.
            const key = `${fieldName}-${index}`;
            const ref = createRef<HTMLInputElement>();
            inputRefs.current[index] = ref;

            return (
              <Input
                ref={ref}
                index={index}
                key={key}
                onChange={validateInput}
                showPinCodeInput={showPinCodeInput}
                providerInputState={providerInputState}
                flexRow
                hideCaption
                alignIcon
              />
            );
          })}
        </StyledInputsContainer>
      )}
      {errorCaption && <Caption message={errorCaption} isError />}
      <Spacer size="xsmall" />
      {/* We rely on `children` to contain a `button` which will submit this form. */}
      {children}
    </StyledForm>
  );
};

export const ProviderInputsGermany = injectIntl(ProviderInputsGermanyPresenter);
